/*
  This page includes code adapted from OpenAI's ChatGPT language model.
  Original author: OpenAI
  Source: https://openai.com
*/

import { Link, useNavigate } from 'react-router-dom';
import React, { useState } from 'react';
import arrow from '../images/arrow.jpeg';
import Popup from '../components/Popup';
import Spinner from '../components/Spinner';

function Login() {
  const navigate = useNavigate();

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginEmailError, setLoginEmailError] = useState('');
  const [loginPasswordError, setLoginPasswordError] = useState('');
  const [createEmail, setCreateEmail] = useState('');
  const [createPassword, setCreatePassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [createEmailError, setCreateEmailError] = useState('');
  const [createPasswordError, setCreatePasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  let url,
      response,
      result;

  const handleLoginEmailChange = (event) => {
    setLoginEmail(event.target.value);
  };

  const handleLoginPasswordChange = (event) => {
    setLoginPassword(event.target.value);
  };

  const handleCreateEmailChange = (event) => {
    setCreateEmail(event.target.value);
  };

  const handleCreatePasswordChange = (event) => {
    setCreatePassword(event.target.value);
  };

  const handleConfirmPasswordChange = (event) => {
    setConfirmPassword(event.target.value);
  };

  const validateEmail = (email) => {
    const errors = [];

    if (email.length === 0) {
      errors.push('A valid email is required');
    } else {
      const atIndex = email.indexOf('@');
      const dotIndex = email.lastIndexOf('.');
      
      if (atIndex === -1) {
        errors.push('Email should contain one @ symbol');
      } else if (dotIndex === -1 || dotIndex < atIndex) {
        errors.push('Email should contain at least one dot after the @ symbol');
      } else if (dotIndex === atIndex+1) {
        errors.push('Email should include a domain name following the @ symbol');
      } else {
        const cctld = email.slice(-6);  // Account for possible country codes (e.g., gov.au)
        const tld = cctld.split('.')
        
        const validTLDs = ['com', 'org', 'net', 'edu', 'gov', 'mil', 'dev', 'co'];
        if (!validTLDs.includes(tld[0]) && !validTLDs.includes(tld[1])) {
          errors.push('Email should have a valid ending (top-level domain)');
        };
      };
    };

    return errors;
  };

  const validatePassword = (password) => {
    const errors = [];

    if (password.length === 0) {
      errors.push('A valid password is required');
    };
  
    return errors;
  };

  const validateConfirmation = (password, confirmPassword) => {
    const errors = [];

    if (confirmPassword.length === 0) {
      errors.push('Please confirm your password');
    };
    if (confirmPassword !== password) {
      errors.push('Passwords do not match');
    };
  
    return errors;
  };


  // Popup message
  const openPopup = () => {
    setIsPopupOpen(true);
  };
  const closePopup = () => {
    setIsPopupOpen(false);
  };

  // log in user
  const handleLoginSubmit = async (event) => {
    event.preventDefault();
    const emailErrors = validateEmail(loginEmail);
    const passwordErrors = validatePassword(loginPassword);
    setLoginEmailError(emailErrors.join('\n'));
    setLoginPasswordError(passwordErrors.join('\n'));
  
    if (emailErrors.length === 0 && passwordErrors.length === 0) {
      setLoginEmail('');
      setLoginPassword('');
      setIsLoading(true);
  
      try {
        // create data
        const loginData = { 
          email: loginEmail,
          password: loginPassword
        };
  
        // send request
        url = `${process.env.REACT_APP_SRV_BASE_URL}/auth/login`;
        response = await fetch(url, { 
          method: 'POST',
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(loginData)
        });
        result = await response.json();
        console.log(result);
  
        // If successful
        if (response.status === 201) {
          // Store token, userId
          localStorage.setItem('token', result.token);
          localStorage.setItem('userId', result.userId);
  
          setTimeout(() => {
            setIsLoading(false);
            navigate('/applications');
          }, 1000); // Added a timeout for better user experience
        } else if (response.status === 404 || 
                   response.status === 500 || 
                   response.status === 401) {
          setPopupMessage(result.message);
          openPopup();
          setIsLoading(false); // Stop the spinner on specific failure codes
        } else {
          setPopupMessage("Unable to log in");
          openPopup();
          setIsLoading(false); // Stop the spinner on general failure
        }
      } catch (err) {
        console.log(err);
        setPopupMessage("An error occurred while trying to log in");
        openPopup();
        setIsLoading(false); // Stop the spinner on error
      }
    } else {
      setIsLoading(false); // Stop the spinner if there are validation errors
    }
  };

  // register new user
  const handleCreateSubmit = async (event) => {
    event.preventDefault();
  
    // Verify user input
    const emailErrors = validateEmail(createEmail);
    const passwordErrors = validatePassword(createPassword);
    const confirmPasswordErrors = validateConfirmation(createPassword, confirmPassword);
    setCreateEmailError(emailErrors.join('\n'));
    setCreatePasswordError(passwordErrors.join('\n'));
    setConfirmPasswordError(confirmPasswordErrors.join('\n'));
  
    if (emailErrors.length === 0 && passwordErrors.length === 0 && confirmPasswordErrors.length === 0) {
      setCreateEmail('');
      setCreatePassword('');
      setConfirmPassword('');
      setIsLoading(true);
  
      try {
        // Create data
        const registerData = {
          email: createEmail,
          password: createPassword
        };
  
        // Send request
        const url = `${process.env.REACT_APP_SRV_BASE_URL}/auth/register`;
        const response = await fetch(url, { 
          method: 'POST',
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(registerData)
        });
        const result = await response.json();
        console.log(result.message);
  
        // Set popup message
        if (response.status === 201) {
          setPopupMessage(result.message);
        } else if (response.status === 404) {
          setPopupMessage(result.message);
        } else if (response.status === 500) {
          setPopupMessage(result.message);
        } else {
          setPopupMessage('User not created');
        }
      } catch (err) {
        console.log(err);
        setPopupMessage("An error occurred while trying to create the user");
      } finally {
        setTimeout(() => {
          setIsLoading(false);
          openPopup();
        }, 1000); // Stops the spinner after 1000 milliseconds and then shows the popup
      }
    }
  };
  
  // useEffect to stop the spinner after 1000 milliseconds
  React.useEffect(() => {
    if (isLoading) {
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isLoading]);
  
  // useEffect to stop the spinner after 1000 milliseconds
  React.useEffect(() => {
    if (isLoading) {
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isLoading]);

  return (
    <div>
      {/* Navbar section */}
      <nav className="navbar">
        <div className="menu-container"></div>
        <h1 className="navbar-title">Job Tracker</h1>
      </nav>

      {/* Slogan-words section */}
      <h1 className="slogan-words">
        <span>Track <br /> Applications</span>
        <img src={arrow} alt="Arrow" className="arrow-img" />
        <span>Gain Insights</span>
        <img src={arrow} alt="Arrow" className="arrow-img" />
        <span>Get the Job</span>
      </h1>
      {isLoading && (
        <div className="loading-overlay">
          <Spinner />
        </div>
      )}
    
      <Popup
        message={popupMessage}
        isOpen={isPopupOpen}
        onClose={closePopup}
      />

      {/* Login-container section */}
      <div className='login-container'>
          <div className="login-card">
            <h2 className="login-heading">Already have an account? Login:</h2>
            <form onSubmit={handleLoginSubmit}>
              <div className='login login-input'>
                <label htmlFor='loginEmail' style={{ display: 'none' }}>Enter login email:</label>
                <input
                  id='loginEmail'
                  type="email"
                  placeholder="Email Address"
                  value={loginEmail}
                  onChange={handleLoginEmailChange}
                  required
                  onBlur={() => {
                    const emailErrors = validateEmail(loginEmail);
                    setLoginEmailError(emailErrors.join('\n'));
                  }}
                />
              </div>
              {loginEmailError && <div className="error">{loginEmailError}</div>}
              <div className='login login-input'>
                <label htmlFor='loginPassword' style={{ display: 'none' }}>Enter login password:</label>
                <input
                  id='loginPassword'
                  type="password"
                  placeholder="Password"
                  value={loginPassword}
                  onChange={handleLoginPasswordChange}
                  required
                  onBlur={() => {
                    const passwordErrors = validatePassword(loginPassword);
                    setLoginPasswordError(passwordErrors.join('\n'));
                  }}
                />
              </div>
              {loginPasswordError && <div className="error">{loginPasswordError}</div>}
              <div className="center">
                <button type="submit" className="login-btn login-button">
                  Login
                </button>
              </div>
              <div className="forgot-password">
                  <span className='center mt-1'><Link to="/forgot-password" className="forgot-password-link">Forgot Password</Link></span>
              </div>
            </form>
          </div>

          <div className="or-divider">
            <h2 className="or-text">OR</h2>
          </div>

          <div className="new-account-card">
            <h2 className="new-account-heading">Create a new account:</h2>
            <form onSubmit={handleCreateSubmit}>
              <div className='new-account login-input'>
                <label htmlFor='createEmail' style={{ display: 'none' }}>Enter new account email:</label>
                <input
                  id='createEmail'
                  type="email"
                  placeholder="Email Address"
                  value={createEmail}
                  onChange={handleCreateEmailChange}
                  required
                  onBlur={() => {
                    const emailErrors = validateEmail(createEmail);
                    setCreateEmailError(emailErrors.join('\n'));
                  }}
                />
              </div>
              {createEmailError && <div className="error">{createEmailError}</div>}
              <div className='new-account login-input'>
                <label htmlFor='createPassword' style={{ display: 'none' }}>Enter new account password:</label>
                <input
                  id='createPassword'
                  type="password"
                  placeholder="Password"
                  value={createPassword}
                  onChange={handleCreatePasswordChange}
                  required
                  onBlur={() => {
                    const passwordErrors = validatePassword(createPassword);
                    setCreatePasswordError(passwordErrors.join('\n'));
                  }}
                />
              </div>
              {createPasswordError && <div className="error">{createPasswordError}</div>}
              <div className='new-account login-input'>
                <label htmlFor='confirmPassword' style={{ display: 'none' }}>Confirm new account password:</label>
                <input
                  id='confirmPassword'
                  type="password"
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  required
                  onBlur={() => {
                    const confirmPasswordErrors = validateConfirmation(createPassword, confirmPassword);
                    setConfirmPasswordError(confirmPasswordErrors.join('\n'));
                  }}
                />
              </div>
              {confirmPasswordError && <div className="error">{confirmPasswordError}</div>}
              <div className="center new-account-btn">
                <button type="submit" className="login-button">
                  Create Account
                </button>
              </div>
            </form>
          </div>
      </div>
    </div>
  );
};

export default Login;
